import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { produce } from 'immer';
import React, { memo, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardsContext } from '../../../../../contexts/DashboardsContext';
import { getDashboardQueriesFunc } from '../../../../../lib/helper';
import theme from '../../../../../theme';
import { Dashboard, DashboardQuery } from '../../../../../types/files';
import SelectLocationModal from '../../../../modals/home-table-view/SelectLocationModal';
import useStyles from "../styles";

const LocationCell: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const { mappedOverviewQueries, setDashboardsQueries } = useContext(DashboardsContext);

    const [locationLoaded, setLocationLoaded] = useState<boolean>(false);
    const [location, setLocation] = useState<string>('');
    const [fieldHover, setFieldHover] = useState<boolean>(false);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const locationRef = useRef<HTMLElement>(null);

    const queryKey = useMemo(() => `${dashboard.id}:Location`, [dashboard.id]);

    useQuery({
        queryKey: ['dashboardQuery', queryKey],
        queryFn: async () => !mappedOverviewQueries.has(queryKey)
            ? await getDashboardQueriesFunc(dashboard.id, 'Location') : [],
        staleTime: 5 * 60 * 1000,
        retry: 2,
        onSuccess: (bulkQueryData: DashboardQuery[]) => {
            setDashboardsQueries(prev => produce(prev, draft => {
                bulkQueryData.forEach(newQuery => {
                    if (!draft.find(query => query.dashboardId === newQuery.dashboardId && query.title === newQuery.title))
                        draft.push(newQuery);
                });
            }));
            setLocationLoaded(true);
        },
    });

    useEffect(() => {
        if (mappedOverviewQueries.has(queryKey)) {
            setLocation(mappedOverviewQueries.get(queryKey) || '');
            setLocationLoaded(true);
        }
    // eslint-disable-next-line
    }, [mappedOverviewQueries]);

    return (<>
        <Stack width="100%" ref={locationRef} onMouseLeave={() => setFieldHover(false)}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%"
                onMouseEnter={() => setFieldHover(true)}>
                {locationLoaded ? (<>
                    <Stack direction="row" spacing={1} alignItems="stretch" justifyContent="flex-start">
                        {(!location || location === 'N/A') ? (
                            <Typography className={classes.plainText} fontWeight={500} color={`${theme.colors.neutral['500']} !important` }>
                                {'No location'}
                            </Typography>
                        ) : (
                            <Typography className={classes.plainText}>{location}</Typography>
                        )}
                    </Stack>
                    {fieldHover && (
                        <Stack direction="row" alignItems="stretch" justifyContent="flex-end">
                            <IconButton disableRipple
                                onClick={() => setEditModalOpen(true)}
                                sx={{ padding: 'unset !important'}}>
                                <EditOutlinedIcon className={classes.icon} sx={{ fill: theme.colors.neutral['500'] }} />
                            </IconButton>
                        </Stack>
                    )}
                </>) : (
                    <Box sx={{ width: '100%' }}>
                        <Skeleton animation="wave" className={classes.wave} />
                    </Box>
                )}
            </Stack>
        </Stack>
        {editModalOpen && (
            <SelectLocationModal
                open={editModalOpen}
                location={location}
                dashboard={dashboard}
                anchorElement={locationRef.current}
                onClose={() => setEditModalOpen(false) } />
        )}
    </>);
}

export default memo(LocationCell);
